import { useState } from "react";
import { keyframes } from "styled-components/macro";
import styled from "styled-components/macro";

import { lightGrey, brightBlue } from "../../../constants/colors";
import { useMount } from "../../../constants/constants";
import { useIsStorybook } from "utils/customHooks/storyHooks";

const keyframeFadeIn = keyframes`
  0% { opacity: 0; }
  100% { opacity: 1; }
`;

const SpinnerFadeInStyle = styled.div<{
  $isAnimationEnabled: boolean;
}>`
  animation: ${props => (props.$isAnimationEnabled ? keyframeFadeIn : "")} 0.5s
    linear;
`;

const keyframeSpin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const SpinnerStyle = styled.div<{
  $isAnimationEnabled: boolean;
  $width?: number;
}>`
  margin: 0 auto;
  border: ${props => (props.$width ? props.$width / 4 : 16)}px solid
    ${lightGrey};
  border-top: ${props => (props.$width ? props.$width / 4 : 16)}px solid
    ${brightBlue};
  border-radius: 50%;
  width: ${props => (props.$width ? props.$width + "px" : "5em")};
  height: ${props => (props.$width ? props.$width + "px" : "5em")};
  animation: ${props => (props.$isAnimationEnabled ? keyframeSpin : "")} 2s
    linear infinite;
`;

type SpinnerProps = {
  hideDuration?: number;
  scale?: number;
  width?: number;
};

/**
 * @param hideDuration milliseconds that the spinner starts out hidden (best practice is usually 500ms)
 * @param scale multiplier to scale the spinner up/down, doesn't change dimensions
 * @param width width in px, else defaults to 5em
 */
export default function Spinner({ hideDuration, scale, width }: SpinnerProps) {
  // start hidden if a hideDuration is passed in
  const [isHidden, setHidden] = useState(Boolean(hideDuration));
  const isStorybook = useIsStorybook();

  // on mount, set a timer to unhide
  useMount(() => {
    let timer: number;
    if (hideDuration) {
      timer = window.setInterval(() => {
        window.clearInterval(timer);
        setHidden(false);
      }, hideDuration);
    }
    // clear the timer on unmount
    return () => {
      if (timer) {
        window.clearInterval(timer);
      }
    };
  });
  return isHidden ? null : (
    <SpinnerFadeInStyle
      $isAnimationEnabled={!isStorybook}
      style={scale ? { transform: `scale(${scale})` } : undefined}
    >
      <SpinnerStyle $isAnimationEnabled={!isStorybook} $width={width} />
    </SpinnerFadeInStyle>
  );
}
